import React, { useEffect, useState, useMemo, useCallback, memo } from "react";
import PropTypes from 'prop-types';
import DataTable from "react-data-table-component";
import {
  crewStyles,
  crewBlankStyles,
} from "../../common/components/crewstyles/CrewStyles";
import ReactTooltip from "react-tooltip";
import CustomerIconBlue from "../../assets/images/Web_Customer_Icon_Blue.svg";
import CustomerIconOrange from "../../assets/images/Web_Customer_Icon_Orange.svg";
import CustomerIconGreen from "../../assets/images/Web_Customer_Icon_Green.svg";
import CustomerIconGray from "../../assets/images/Web_Customer_Icon_Gray.svg";
import CustomerIconTlink from "../../assets/images/Web_Customer_Icon_Tlink.svg";
import PreviousTaskContainer from "./PreviousTaskContainer.js";

/**
 * Optimized function to get customer icon based on status and tlink
 * Uses lookup map (ENG-3.1: reduce cognitive complexity)
 */
const STATUS_ICON_MAP = {
  ASSIGNED: CustomerIconBlue,
  CONFIRMED: CustomerIconOrange,
  ROVR_CONFIRMED: CustomerIconOrange,
  STARTED: CustomerIconGreen,
  FINISHED: CustomerIconGray,
};

const getCustomerIcon = (status, tlink) => {
  if (tlink && status === "STARTED") return CustomerIconTlink;
  return STATUS_ICON_MAP[status] || CustomerIconGray;
};

/**
 * Optimized CrewComponent for displaying crew data in a table format
 * @param {Object} props - Component props
 * @param {Array} props.crewList - List of crew members
 * @param {string} props.crewInfo - Type of crew (care/operations)
 * @param {string} props.empName - Employee name
 * @param {boolean} props.showNameFlag - Flag to show names
 * @param {boolean} props.isCrewChief - Flag if user is crew chief
 * @param {string} props.role - User role
 * @param {Object} props.shiftInfoList - Shift information
 * @param {Object} props.fetchDetailsParams - Parameters for fetching details
 * @param {boolean} props.isSingleAgent - Flag for single agent
 * @returns {JSX.Element} Rendered crew component
 */
const CrewComponent = memo(({
  crewList = [],
  crewInfo,
  empName,
  showNameFlag,
  isCrewChief,
  role,
  shiftInfoList,
  fetchDetailsParams,
  isSingleAgent
}) => {
  const [tableData, setTableData] = useState([]);

  // Memoize admin flag to prevent recalculation
  const adminFlag = useMemo(() => {
    return role === "Rovr_RT_Adm" ||
           role === "Rovr_Admin" ||
           role === "Rovr_Super_Admin";
  }, [role]);

  // Optimize update function with useCallback
  const update = useCallback((previousTask) => {
    setTableData(prevData => 
      prevData.map((item) => {
        if (item.employeeId === previousTask.userId) {
          return { ...item, previousTask };
        }
        return item;
      })
    );
  }, []);

  // Memoize getRole function - simplified with early returns
  const getRole = useCallback((taskRole, taskRole2, taskRoleEligible) => {
    if (!taskRoleEligible) return null;

    const roleStyles = { color: "blue", fontSize: "12px" };
    const roleText = taskRole && taskRole2 ? `${taskRole} - ${taskRole2}`
      : taskRole || taskRole2 || null;

    if (roleText) {
      return <span style={roleStyles}> ({roleText})</span>;
    }
    return <b style={{ ...roleStyles, fontWeight: "bold" }}>{" ( - )"}</b>;
  }, []);

  // Memoize table data processing
  const processedTableData = useMemo(() => {
    return crewList.map((item, index) => {
      const baseData = {
        Task: item.taskName,
        id: `${item.userId || 'user'}-${item.taskId || 'task'}-${item.startTime || 'start'}-${index}`,
        StartTime: item.startTime,
        EndTime: item.endTime,
        TaskStatus: item.taskStatus,
        TLinkRoute: item.tlinkRoute,
        taskRole: item.taskRole,
        taskRole2: item.taskRole2,
        taskRoleEligible: item.taskRoleEligible,
        employeeId: item.userId,
        taskId: item.taskId,
        previousTask: undefined,
        isSingleAgent: item.singleAgent
      };

      if (item.firstName && item.lastName) {
        const ellipses = item.lastName.length < 8 ? "" : "...";
        const name = item.firstName.charAt(0) + "." + item.lastName.slice(0, 8) + ellipses;
        const fullName = item.firstName + " " + item.lastName;
        const masked = !adminFlag && item.ackRequired && !showNameFlag;

        return {
          ...baseData,
          Name: masked ? "Assigned" : name,
          FullName: masked ? "Assigned" : fullName,
        };
      } else {
        return {
          ...baseData,
          Name: item.lastName,
        };
      }
    });
  }, [crewList, adminFlag, showNameFlag]);

  // Update table data when processed data changes
  useEffect(() => {
    setTableData(processedTableData);
  }, [processedTableData]);

  // Memoize columns configuration for better performance
  const columns = useMemo(() => {
    const baseColumns = [
      {
        width: "30px",
        sortable: false,
        cell: (row) => (
          <img 
            src={getCustomerIcon(row.TaskStatus, row.TLinkRoute)}
            alt={`Status icon for ${row.TaskStatus || 'task'}`}
          />
        ),
      },
      {
        name: crewInfo === "care" ? "Customer Care" : "Customer Operations",
        selector: "Name",
        sortable: false,
        cell: (row) => (
          <div>
            <div data-tip={row.FullName} data-for={row.id}>
              {row.Name}
              {crewInfo !== "care" && getRole(row.taskRole, row.taskRole2, row.taskRoleEligible)}
            </div>
            <ReactTooltip 
              id={row.id} 
              place="right" 
              effect="solid" 
              backgroundColor="#36495A"
            />
          </div>
        ),
      },
      {
        name: "Task",
        selector: "Task",
        sortable: false,
        cell: (row) => (
          <div data-for={row.id}>
            {row.Task}
            {((row.isSingleAgent !== undefined && 
              ((crewInfo === "care" && !row.isSingleAgent) || 
               (crewInfo !== "care" && row.isSingleAgent)))) && (
              <span style={{ fontWeight: 'bold' }}>
                {' (SAB)'}
              </span>
            )}
          </div>
        ),
      },
      {
        name: "Start Time",
        selector: "StartTime",
        sortable: false,
      },
      {
        name: "End Time",
        selector: "EndTime",
        sortable: false,
      },
    ];
    
    return baseColumns;
  }, [crewInfo, getRole]);

  // Memoize expandable icons to prevent re-creation
  const expandableIcons = useMemo(() => ({
    collapsed: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 40 31.5"
        aria-label="Expand row"
      >
        <path id="Shape" d="M0,0,11.5,11.5,23,0Z" fill="rgba(0,0,0,0.54)" />
      </svg>
    ),
    expanded: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 40 31.5"
        aria-label="Collapse row"
      >
        <path id="Shape" d="M0,11.5,11.5,0,23,11.5Z" fill="rgba(0,0,0,0.54)" />
      </svg>
    ),
  }), []);

  // Memoize expandable row component
  const expandableRowComponent = useMemo(() => (
    <PreviousTaskContainer
      fetchDetailsParams={fetchDetailsParams}
      update={update}
    />
  ), [fetchDetailsParams, update]);

  // Memoize empty data placeholder
  const emptyDataPlaceholder = useMemo(() => [
    { Name: "...", Task: "...", StartTime: "..." }
  ], []);

  return (
    <div>
      {tableData.length > 0 ? (
        <DataTable
          columns={columns}
          customStyles={crewStyles}
          data={tableData}
          noHeader
          responsive={false}
          fixedHeader
          persistTableHead
          expandableIcon={expandableIcons}
          expandableRows
          expandableRowsComponent={expandableRowComponent}
        />
      ) : (
        <DataTable
          columns={columns}
          customStyles={crewBlankStyles}
          data={emptyDataPlaceholder}
          noHeader
          fixedHeader
          persistTableHead
        />
      )}
    </div>
  );
});

// PropTypes for type checking
CrewComponent.propTypes = {
  crewList: PropTypes.arrayOf(PropTypes.shape({
    userId: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    taskName: PropTypes.string,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    taskStatus: PropTypes.string,
    tlinkRoute: PropTypes.string,
    taskRole: PropTypes.string,
    taskRole2: PropTypes.string,
    taskRoleEligible: PropTypes.bool,
    taskId: PropTypes.string,
    ackRequired: PropTypes.bool,
    singleAgent: PropTypes.bool,
  })),
  crewInfo: PropTypes.string,
  empName: PropTypes.string,
  showNameFlag: PropTypes.bool,
  isCrewChief: PropTypes.bool,
  role: PropTypes.string,
  shiftInfoList: PropTypes.object,
  fetchDetailsParams: PropTypes.object,
  isSingleAgent: PropTypes.bool,
};

CrewComponent.defaultProps = {
  crewList: [],
  showNameFlag: false,
  isCrewChief: false,
  isSingleAgent: false,
};

export default CrewComponent;
